import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/auth";

// Loads a PayPal JS SDK script exactly once per (client_id, params, namespace) tuple.
const _sdkLoads = {};
function loadPayPalSdk({ clientId, params, namespace }) {
  const key = `${namespace}::${JSON.stringify(params)}`;
  if (_sdkLoads[key]) return _sdkLoads[key];
  _sdkLoads[key] = new Promise((resolve, reject) => {
    if (window[namespace]) return resolve(window[namespace]);
    const q = new URLSearchParams({ "client-id": clientId, ...params });
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?${q.toString()}`;
    s.setAttribute("data-namespace", namespace);
    s.async = true;
    s.onload = () => resolve(window[namespace]);
    s.onerror = () => reject(new Error("Failed to load PayPal SDK"));
    document.head.appendChild(s);
  });
  return _sdkLoads[key];
}

/**
 * planKey — one of: plus_monthly, plus_yearly, pro_monthly, pro_yearly, lifetime
 * onClose — closes modal
 * onSuccess — called with { plan } after backend confirms upgrade
 */
export default function PayPalCheckoutModal({ planKey, onClose, onSuccess }) {
  const containerRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | processing | error
  const [errorMsg, setErrorMsg] = useState("");

  const isLifetime = planKey === "lifetime";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/paypal/config");
        if (cancelled) return;
        setCfg(data);
        const params = isLifetime
          ? { currency: data.currency, intent: "capture", components: "buttons" }
          : { currency: data.currency, vault: "true", intent: "subscription", components: "buttons" };
        const namespace = isLifetime ? "paypal_cap" : "paypal_sub";
        const paypal = await loadPayPalSdk({ clientId: data.client_id, params, namespace });
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";

        const buttons = paypal.Buttons(
          isLifetime
            ? {
                style: { layout: "vertical", shape: "pill", label: "pay", color: "gold" },
                createOrder: async () => {
                  const { data: r } = await apiClient.post("/paypal/orders", { plan_key: "lifetime" });
                  if (!r.id) throw new Error("Could not create order");
                  return r.id;
                },
                onApprove: async (dataApp) => {
                  try {
                    setStatus("processing");
                    const { data: r } = await apiClient.post(`/paypal/orders/${dataApp.orderID}/capture`);
                    if (r?.ok) {
                      toast.success("Lifetime access unlocked. Bienvenue!");
                      onSuccess?.({ plan: "lifetime" });
                    } else {
                      throw new Error("Capture failed");
                    }
                  } catch (e) {
                    setStatus("error");
                    setErrorMsg(e?.response?.data?.detail || e.message || "Payment capture failed");
                  }
                },
                onError: (err) => {
                  setStatus("error");
                  setErrorMsg(err?.message || "PayPal returned an error");
                },
                onCancel: () => {
                  toast("Checkout cancelled.", { description: "You can try again anytime." });
                },
              }
            : {
                style: { layout: "vertical", shape: "pill", label: "subscribe", color: "gold" },
                createSubscription: async (_d, actions) => {
                  const plan = data.plans[planKey];
                  if (!plan?.plan_id) throw new Error("Plan not available");
                  return actions.subscription.create({
                    plan_id: plan.plan_id,
                    custom_id: `alp:${planKey}`,
                  });
                },
                onApprove: async (dataApp) => {
                  try {
                    setStatus("processing");
                    const { data: r } = await apiClient.post("/paypal/subscriptions/verify", {
                      plan_key: planKey,
                      subscription_id: dataApp.subscriptionID,
                    });
                    if (r?.ok) {
                      toast.success(`${r.plan[0].toUpperCase() + r.plan.slice(1)} activated. On y va !`);
                      onSuccess?.({ plan: r.plan });
                    } else {
                      throw new Error("Verification failed");
                    }
                  } catch (e) {
                    setStatus("error");
                    setErrorMsg(e?.response?.data?.detail || e.message || "Subscription verification failed");
                  }
                },
                onError: (err) => {
                  setStatus("error");
                  setErrorMsg(err?.message || "PayPal returned an error");
                },
                onCancel: () => {
                  toast("Checkout cancelled.", { description: "You can try again anytime." });
                },
              }
        );

        if (buttons.isEligible && !buttons.isEligible()) {
          setStatus("error");
          setErrorMsg("PayPal isn't available for this device. Try another browser or contact support.");
          return;
        }
        await buttons.render(containerRef.current);
        setStatus("ready");
      } catch (e) {
        setStatus("error");
        setErrorMsg(e?.message || "Could not load checkout");
      }
    })();
    return () => { cancelled = true; };
  }, [planKey]);

  const planLabel = ({
    plus_monthly: "Plus · €17.99 / month",
    plus_yearly: "Plus · €149 / year",
    pro_monthly: "Pro · €29.99 / month",
    pro_yearly: "Pro · €299 / year",
    lifetime: "Lifetime · €499 (launch price)",
  })[planKey] || planKey;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="paypal-modal-backdrop"
    >
      <div
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#15102E] p-6 md:p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="paypal-modal"
      >
        <button
          className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
          onClick={onClose}
          aria-label="Close checkout"
          data-testid="paypal-modal-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-1">Checkout</div>
        <h3 className="font-heading text-2xl font-bold tracking-tight">{planLabel}</h3>
        <p className="text-sm text-[#A89BC9] mt-1">
          You&apos;ll be redirected to PayPal. 7-day money-back guarantee — cancel anytime.
        </p>

        <div className="mt-5 min-h-[220px]">
          {status === "loading" && (
            <div className="flex items-center justify-center py-10 text-[#cdc1ed]" data-testid="paypal-modal-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading PayPal…
            </div>
          )}
          {status === "processing" && (
            <div className="flex items-center justify-center py-10 text-mint-300" data-testid="paypal-modal-processing">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Confirming your payment…
            </div>
          )}
          {status === "error" && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200" data-testid="paypal-modal-error">
              {errorMsg}
            </div>
          )}
          <div ref={containerRef} data-testid="paypal-buttons-container" />
        </div>

        <div className="mt-5 flex items-center gap-2 text-[11px] text-[#7d6fa7]">
          <ShieldCheck className="h-3.5 w-3.5" />
          Secure checkout by PayPal · {cfg?.mode === "live" ? "Live" : "Sandbox"} · EUR
        </div>
      </div>
    </div>
  );
}
